/**
 * useConcurrencyRules — gates the Draw / Resume buttons against the
 * In Progress rules (CLAUDE.md §7.6).
 *
 * Rules:
 *   MAX-1: At most one file may have status='in_progress' at any
 *          time, irrespective of who drew it.
 *   MANUAL-MOVES-REQUIRE-EMPTY-SLOT:
 *          Manual moves from Submitted → In Progress and Paused →
 *          In Progress (resume) are allowed only when In Progress
 *          is empty. Blocked otherwise.
 *
 * The activeFile is the global in_progress file (if any) — not
 * filtered by drawn_by — so the gate is identical for Marinus and
 * Wouter. Paused-file listing remains per-user for the assistant's
 * paused-file count but does not affect the draw gate.
 */

import { useMemo } from 'react';

export default function useConcurrencyRules(files, userEmail /* role unused — kept for backwards compatibility */) {
  return useMemo(() => {
    // Test records (is_test = true, CLAUDE.md §33) are released from the
    // In Progress rules: an in-progress test file never occupies the
    // MAX-1 slot, so it never blocks a real Draw / Resume.
    const list = (Array.isArray(files) ? files : []).filter((f) => f.isTest !== true);

    // Global In Progress slot — at most one file, irrespective of who
    // drew it. This is the invariant that gates Draw and Resume.
    const activeFiles = list.filter((f) => f.status === 'in_progress');
    const activeFile = activeFiles[0] || null;

    // Per-user paused count is informational only (assistant dashboard
    // shows it). Pause itself is never blocked — when other paused
    // files exist the picker handles the inbound replacement; when not,
    // the action commits immediately and In Progress goes empty.
    const pausedFiles = list.filter(
      (f) => f.status === 'paused' && (userEmail ? f.drawnBy === userEmail : true)
    );

    const canDrawFile = !activeFile;
    const canPauseFile = !!activeFile;

    // Per-record movement gate (CLAUDE.md §33.4). Two separate halves of
    // the test-record invariant meet here:
    //   (a) test records never OCCUPY the slot — the realFiles filter
    //       above keeps an in-progress test file out of `activeFile`;
    //   (b) a movement action ON a test record is never itself blocked —
    //       this gate short-circuits to "allowed" for is_test records
    //       BEFORE any slot evaluation, for every caller and every role.
    // Real records get exactly the pre-existing slot rule (!activeFile).
    const canMoveFile = (record) => record?.isTest === true || !activeFile;

    const message = activeFile
      ? 'Only one file can be in progress at a time.'
      : null;

    return {
      canDrawFile,
      canPauseFile,
      canMoveFile,
      activeFile,
      pausedFiles,
      activeCount: activeFiles.length,
      pausedCount: pausedFiles.length,
      message,
    };
  }, [files, userEmail]);
}
